import clsx from 'clsx';
import type { ReactNode } from 'react';

import type { ColourScale } from '~utils/colour-scale';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

type LegendEntry = ColourScale['legend'][number];

type ColourLegendProps = {
  /** The colour scale to display in the legend */
  colourScale: ColourScale;
  /** Optional title for the legend (defaults to 'Colour Scale') */
  title?: string;
  /** Whether to show the property used to generate the colour scale in the title (defaults to true) */
  showProperty?: boolean;
  /** Optional callback to visually dim specific legend rows */
  isItemDimmed?: (entry: LegendEntry) => boolean;
  /** Optional renderer for row accessory controls (e.g. toggles) */
  renderItemAccessory?: (entry: LegendEntry) => ReactNode;
};

/** Extracts CSS style from either string colour or ColourValue object */
const getColourStyle = (colour: string | { fill: string; opacity: number }) =>
  typeof colour === 'string' ? { backgroundColor: colour } : { backgroundColor: colour.fill, opacity: colour.opacity };

/**
 * Renders a legend for any colour scale (continuous or discrete)
 */
const ColourLegend = ({
  colourScale,
  title = 'Colour Scale',
  showProperty = true,
  isItemDimmed,
  renderItemAccessory,
}: ColourLegendProps) => (
  <Card>
    <CardHeader>
      <CardTitle className="font-medium text-foreground text-sm">
        {title}
        {showProperty && <span className="text-muted-foreground text-xs"> ({colourScale.property})</span>}
      </CardTitle>
    </CardHeader>
    <CardContent>
      {colourScale.legend.map((entry) => {
        const isDimmed = isItemDimmed?.(entry) ?? false;

        return (
          <div key={`${String(entry.value)}-${entry.label}`} className="mb-2 flex items-center gap-2">
            <div
              className={clsx('h-4 w-8 rounded-sm', isDimmed && 'opacity-50')}
              style={getColourStyle(entry.colour)}
            />
            <span className={clsx('text-sm', isDimmed ? 'text-muted-foreground opacity-50' : 'text-foreground')}>
              {entry.label}
            </span>
            {renderItemAccessory && <div className="ml-auto">{renderItemAccessory(entry)}</div>}
          </div>
        );
      })}
    </CardContent>
  </Card>
);

export { ColourLegend };
